/* =====================================================================
   Server-driven primary brand color
   ---------------------------------------------------------------------
   The tenant picks a primary color in the ERPat App Dash editor; it rides
   the published layout as `theme.primary` and reaches the client on
   `GET /v1/api/me/app-dashboard?app=syntry` (see mapAppDashboard →
   AppDashboardVM.themePrimary). This module turns that enum key into a
   single injected <style> element that overrides the brand tokens.

   Base values live in `src/theme/tokens.scss` (the --syntry-* tokens) and
   `src/theme/variables.scss` (--ion-color-primary-rgb). Nothing here is
   computed at runtime — every value below is PRECOMPUTED and copied
   verbatim from the shared backend/frontend contract, so the PHP palette,
   the editor swatches and this table can never drift.

   Derivation rule used to produce the tables (recorded, not executed):
   the source hue of #009DA8 is 183.93°. For every teal-derived token the
   hex is converted to HSL, S / L / alpha are kept, and the hue is set to
   `H + (targetBaseHue - 183.93)` before converting back to hex. Target
   base hues: red 0°, blue 210°, green 145°, orange 30°, yellow 48°. RGB
   triplets are recomputed from the rotated hexes, and gradient stops are
   rotated individually so each gradient keeps the original teal→mint hue
   spread rotated into the new family.

   `default` deliberately has NO table: applying it REMOVES the injected
   element so the bundled tokens stand unmodified (zero drift risk).

   --syntry-gradient-wave-1..6 (the Personal Info hero's decorative wave
   fills/stroke) are overridden the same way as the gradient stops, rotated
   individually so the texture keeps its light/mid/shadow spread in the new
   family instead of leaving a teal/mint wash over a non-teal hero.

   Not overridden, on purpose: --syntry-mark/-dark/-soft/-rgb, which carry the
   Syntry product identity (the header wordmark, the image-less avatar and the
   bottom tab bar's active label/indicator) and must stay teal whatever the
   tenant picks — that is why they exist as their own tokens instead of
   reusing --syntry-primary*. Also not overridden because
   they are not teal-derived in the current cascade: --syntry-cyan/-soft,
   --syntry-blue/-soft, --syntry-gradient-soft, --erpat-primary (ERPat's own
   mark) and --ion-color-primary-contrast. The red dark block owns a slightly
   lifted gradient so dark UI accents do not collapse into a blood-red stop.
   ===================================================================== */

import { computed, ref, type ComputedRef } from 'vue';

export type BrandColorKey = 'default' | 'red' | 'blue' | 'green' | 'orange' | 'yellow';

/** Every valid key, in contract order. */
export const BRAND_COLOR_KEYS: readonly BrandColorKey[] = [
  'default',
  'red',
  'blue',
  'green',
  'orange',
  'yellow',
];

/** localStorage key holding the last applied brand color. */
export const STORAGE_KEY = 'syntry_brand_color';

/** id of the injected <style> element (also its removal handle). */
export const STYLE_ID = 'syntry-brand-color';

/** The bundled light-mode primary — restored whenever the key is `default`. */
const DEFAULT_PRIMARY = '#009DA8';

/**
 * Reactive mirror of the key currently painted on the document, written by
 * {@link applyBrandColor} — the one funnel every path goes through (pre-mount
 * restore, the Home layout watch, sign-out reset). Components that need to
 * *branch* on the tenant color rather than merely consume a token read this.
 *
 * Kept private; the two derived refs below are the public surface so nothing
 * outside this module can set the key without also painting the CSS.
 */
const activeKey = ref<BrandColorKey>('default');

/** The brand color currently applied to the document. */
export const brandColorKey: ComputedRef<BrandColorKey> = computed(() => activeKey.value);

/**
 * True when the tenant has picked a color, i.e. the brand tokens are overridden
 * and the app should be showing that color rather than the bundled teal.
 *
 * The tokens alone can't answer this — `--syntry-primary` always resolves to
 * *something* — so a component that must drop a decorative palette in favour of
 * the tenant's single color (the Home quick-action tiles) branches on this.
 */
export const hasCustomBrandColor: ComputedRef<boolean> = computed(
  () => activeKey.value !== 'default',
);

interface BrandPalette {
  /** Light-mode primary hex; also the <meta name="theme-color"> value. */
  primary: string;
  /** Token overrides emitted into the `:root` block. */
  light: Readonly<Record<string, string>>;
  /** Token overrides emitted into the `body.dark` block. */
  dark: Readonly<Record<string, string>>;
}

/** Precomputed token tables, keyed by enum value. `default` has none. */
const PALETTES: Readonly<Record<Exclude<BrandColorKey, 'default'>, BrandPalette>> = {
  red: {
    primary: '#A80000',
    light: {
      '--syntry-primary': '#A80000',
      '--syntry-primary-dark': '#890400',
      '--syntry-primary-light': '#F8E6E7',
      '--syntry-teal': '#AF000B',
      '--syntry-teal-soft': '#F7E0E2',
      '--syntry-gradient-primary':
        'linear-gradient(135deg, #B7150E 0%, #C21F44 50%, #D93472 100%)',
      '--syntry-gradient-primary-start': '#B7150E',
      '--syntry-gradient-primary-mid': '#C21F44',
      '--syntry-gradient-primary-end': '#D93472',
      '--syntry-gradient-wave-1': '#FFD7E1',
      '--syntry-gradient-wave-2': '#FBBFCC',
      '--syntry-gradient-wave-3': '#EA5E7D',
      '--syntry-gradient-wave-4': '#9F0F1D',
      '--syntry-gradient-wave-5': '#FFEFF4',
      '--syntry-gradient-wave-6': '#FFF2F6',
      '--syntry-shadow-button': '0 12px 24px rgba(168, 0, 0, 0.28)',
      '--ion-color-primary-rgb': '168, 0, 0',
    },
    dark: {
      '--syntry-primary': '#D9363F',
      '--syntry-primary-dark': '#C82634',
      '--syntry-primary-light': 'rgba(217, 54, 63, 0.16)',
      '--syntry-teal': '#D9363F',
      '--syntry-teal-soft': 'rgba(217, 54, 63, 0.16)',
      '--syntry-gradient-primary':
        'linear-gradient(135deg, #D9363F 0%, #E24861 50%, #F06B90 100%)',
      '--syntry-gradient-primary-start': '#D9363F',
      '--syntry-gradient-primary-mid': '#E24861',
      '--syntry-gradient-primary-end': '#F06B90',
      '--syntry-brand-soft-color':
        'color-mix(in srgb, var(--syntry-surface) 92%, var(--syntry-primary) 8%)',
      '--syntry-brand-soft-surface':
        'linear-gradient(90deg, color-mix(in srgb, var(--syntry-primary) 0%, transparent) 0%, color-mix(in srgb, var(--syntry-primary) 44%, var(--syntry-surface) 26%) 48%, color-mix(in srgb, var(--syntry-primary) 0%, transparent) 100%) bottom / 100% 1px no-repeat, linear-gradient(180deg, color-mix(in srgb, var(--syntry-primary) 0%, transparent) 0%, color-mix(in srgb, var(--syntry-primary) 7%, transparent) 58%, color-mix(in srgb, var(--syntry-primary) 18%, var(--syntry-surface) 10%) 100%), linear-gradient(180deg, var(--syntry-surface) 0%, var(--syntry-surface) 54%, color-mix(in srgb, var(--syntry-surface) 92%, var(--syntry-primary) 8%) 100%)',
      '--syntry-brand-soft-foreground': '#FFDCE5',
      '--syntry-brand-soft-foreground-fill': 'transparent',
      '--syntry-brand-soft-foreground-gradient':
        'linear-gradient(90deg, #FFF2F6 0%, #FBBFCC 34%, #FB8EA0 68%, #F46F8A 100%)',
      '--syntry-shadow-button': '0 12px 24px rgba(217, 54, 63, 0.30)',
      '--ion-color-primary-rgb': '217, 54, 63',
    },
  },
  blue: {
    primary: '#0054A8',
    light: {
      '--syntry-primary': '#0054A8',
      '--syntry-primary-dark': '#004089',
      '--syntry-primary-light': '#E6F0F8',
      '--syntry-teal': '#0063AF',
      '--syntry-teal-soft': '#E0EDF7',
      '--syntry-gradient-primary':
        'linear-gradient(135deg, #0E5CB7 0%, #1F95C2 50%, #34C4D9 100%)',
      '--syntry-gradient-primary-start': '#0E5CB7',
      '--syntry-gradient-primary-mid': '#1F95C2',
      '--syntry-gradient-primary-end': '#34C4D9',
      '--syntry-gradient-wave-1': '#D7F5FF',
      '--syntry-gradient-wave-2': '#BFEAFB',
      '--syntry-gradient-wave-3': '#5EC3EA',
      '--syntry-gradient-wave-4': '#0F659F',
      '--syntry-gradient-wave-5': '#EFFCFF',
      '--syntry-gradient-wave-6': '#F2FCFF',
      '--syntry-shadow-button': '0 12px 24px rgba(0, 84, 168, 0.28)',
      '--ion-color-primary-rgb': '0, 84, 168',
    },
    dark: {
      '--syntry-primary': '#1677CF',
      '--syntry-primary-dark': '#0E5CB7',
      '--syntry-primary-light': 'rgba(22, 119, 207, 0.18)',
      '--syntry-teal': '#1677CF',
      '--syntry-teal-soft': 'rgba(22, 119, 207, 0.18)',
      '--syntry-shadow-button': '0 12px 24px rgba(22, 119, 207, 0.34)',
      '--ion-color-primary-rgb': '22, 119, 207',
    },
  },
  green: {
    primary: '#00A846',
    light: {
      '--syntry-primary': '#00A846',
      '--syntry-primary-dark': '#00893D',
      '--syntry-primary-light': '#E6F8EC',
      '--syntry-teal': '#00AF3D',
      '--syntry-teal-soft': '#E0F7E8',
      '--syntry-gradient-primary':
        'linear-gradient(135deg, #0EB75B 0%, #1FC23E 50%, #34D93B 100%)',
      '--syntry-gradient-primary-start': '#0EB75B',
      '--syntry-gradient-primary-mid': '#1FC23E',
      '--syntry-gradient-primary-end': '#34D93B',
      '--syntry-gradient-wave-1': '#D7FFDE',
      '--syntry-gradient-wave-2': '#BFFBCB',
      '--syntry-gradient-wave-3': '#5EEA79',
      '--syntry-gradient-wave-4': '#0F9F3D',
      '--syntry-gradient-wave-5': '#EFFFF1',
      '--syntry-gradient-wave-6': '#F2FFF4',
      '--syntry-shadow-button': '0 12px 24px rgba(0, 168, 70, 0.28)',
      '--ion-color-primary-rgb': '0, 168, 70',
    },
    dark: {
      '--syntry-primary': '#16CF5F',
      '--syntry-primary-dark': '#0EB75B',
      '--syntry-primary-light': 'rgba(22, 207, 95, 0.18)',
      '--syntry-teal': '#16CF5F',
      '--syntry-teal-soft': 'rgba(22, 207, 95, 0.18)',
      '--syntry-shadow-button': '0 12px 24px rgba(22, 207, 95, 0.34)',
      '--ion-color-primary-rgb': '22, 207, 95',
    },
  },
  orange: {
    primary: '#A85400',
    light: {
      '--syntry-primary': '#A85400',
      '--syntry-primary-dark': '#894900',
      '--syntry-primary-light': '#F8EEE6',
      '--syntry-teal': '#AF4C00',
      '--syntry-teal-soft': '#F7EAE0',
      '--syntry-gradient-primary':
        'linear-gradient(135deg, #B7690E 0%, #C24C1F 50%, #D94934 100%)',
      '--syntry-gradient-primary-start': '#B7690E',
      '--syntry-gradient-primary-mid': '#C24C1F',
      '--syntry-gradient-primary-end': '#D94934',
      '--syntry-gradient-wave-1': '#FFE1D7',
      '--syntry-gradient-wave-2': '#FBD0BF',
      '--syntry-gradient-wave-3': '#EA855E',
      '--syntry-gradient-wave-4': '#9F490F',
      '--syntry-gradient-wave-5': '#FFF2EF',
      '--syntry-gradient-wave-6': '#FFF5F2',
      '--syntry-shadow-button': '0 12px 24px rgba(168, 84, 0, 0.28)',
      '--ion-color-primary-rgb': '168, 84, 0',
    },
    dark: {
      '--syntry-primary': '#CF6E16',
      '--syntry-primary-dark': '#B7690E',
      '--syntry-primary-light': 'rgba(207, 110, 22, 0.18)',
      '--syntry-teal': '#CF6E16',
      '--syntry-teal-soft': 'rgba(207, 110, 22, 0.18)',
      '--syntry-shadow-button': '0 12px 24px rgba(207, 110, 22, 0.34)',
      '--ion-color-primary-rgb': '207, 110, 22',
    },
  },
  yellow: {
    primary: '#A88600',
    light: {
      '--syntry-primary': '#A88600',
      '--syntry-primary-dark': '#897200',
      '--syntry-primary-light': '#F8F3E6',
      '--syntry-teal': '#AF8100',
      '--syntry-teal-soft': '#F7F1E0',
      '--syntry-gradient-primary':
        'linear-gradient(135deg, #B79C0E 0%, #C27D1F 50%, #D97A34 100%)',
      '--syntry-gradient-primary-start': '#B79C0E',
      '--syntry-gradient-primary-mid': '#C27D1F',
      '--syntry-gradient-primary-end': '#D97A34',
      '--syntry-gradient-wave-1': '#FFEDD7',
      '--syntry-gradient-wave-2': '#FBE2BF',
      '--syntry-gradient-wave-3': '#EAAF5E',
      '--syntry-gradient-wave-4': '#9F740F',
      '--syntry-gradient-wave-5': '#FFF7EF',
      '--syntry-gradient-wave-6': '#FFF9F2',
      '--syntry-shadow-button': '0 12px 24px rgba(168, 134, 0, 0.28)',
      '--ion-color-primary-rgb': '168, 134, 0',
    },
    dark: {
      '--syntry-primary': '#CFA616',
      '--syntry-primary-dark': '#B79C0E',
      '--syntry-primary-light': 'rgba(207, 166, 22, 0.18)',
      '--syntry-teal': '#CFA616',
      '--syntry-teal-soft': 'rgba(207, 166, 22, 0.18)',
      '--syntry-shadow-button': '0 12px 24px rgba(207, 166, 22, 0.34)',
      '--ion-color-primary-rgb': '207, 166, 22',
    },
  },
};

/** Anything that isn't one of the six enum strings collapses to `default`. */
export function normalizeBrandColorKey(value: unknown): BrandColorKey {
  return typeof value === 'string' && (BRAND_COLOR_KEYS as readonly string[]).includes(value)
    ? (value as BrandColorKey)
    : 'default';
}

function hasDom(): boolean {
  return typeof document !== 'undefined';
}

function block(selector: string, tokens: Readonly<Record<string, string>>): string {
  const body = Object.entries(tokens)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}

/**
 * The CSS text for a palette key — a `:root` block plus a `body.dark` block.
 * Exported for offline verification; the app goes through {@link applyBrandColor}.
 */
export function brandColorCss(key: BrandColorKey): string {
  if (key === 'default') return '';
  const palette = PALETTES[key];
  return `${block(':root', palette.light)}\n${block('body.dark', palette.dark)}`;
}

/** The light-mode primary hex for a key (used for <meta name="theme-color">). */
export function brandColorPrimary(key: BrandColorKey): string {
  return key === 'default' ? DEFAULT_PRIMARY : PALETTES[key].primary;
}

function setThemeColorMeta(hex: string): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', hex);
}

function setBrandColorDataset(key: BrandColorKey): void {
  if (key === 'default') {
    document.body.removeAttribute('data-syntry-brand');
    return;
  }

  document.body.dataset.syntryBrand = key;
}

/**
 * Apply a brand color to the live document.
 *
 * `default` removes the injected element so the bundled tokens.scss /
 * variables.scss values stand untouched. Any other key writes a single
 * <style> **appended to `document.head`** — appending is what makes it win at
 * equal specificity (`:root` vs `:root`, `body.dark` vs `body.dark`) in both
 * dev, where Vite injects the SCSS as earlier <style> tags, and production,
 * where it is an earlier <link rel="stylesheet">. Document order decides, so
 * the element must stay last.
 */
export function applyBrandColor(key: BrandColorKey): void {
  if (!hasDom()) return;

  const existing = document.getElementById(STYLE_ID);
  if (key === 'default') {
    existing?.remove();
    setThemeColorMeta(DEFAULT_PRIMARY);
    setBrandColorDataset('default');
    activeKey.value = 'default';
    return;
  }

  const style = existing instanceof HTMLStyleElement ? existing : document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = brandColorCss(key);
  // Re-append even when it already exists: any stylesheet added since (a lazy
  // route chunk's CSS, an HMR update) would otherwise sit after it and win.
  document.head.appendChild(style);

  setThemeColorMeta(brandColorPrimary(key));
  setBrandColorDataset(key);
  // Last, and only on the paths that actually painted: the reactive key must
  // never claim a color the document isn't wearing (the no-DOM guard above
  // returns before this, leaving it at `default`).
  activeKey.value = key;
}

/** Persist and apply a brand color. Unknown values collapse to `default`. */
export function setBrandColor(key: BrandColorKey): void {
  const normalized = normalizeBrandColorKey(key);
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
  } catch {
    /* private mode / storage disabled — the style is still applied in-memory */
  }
  applyBrandColor(normalized);
}

/**
 * Apply the persisted brand color. Called at module scope in main.ts so the
 * tokens are in place before the first paint (no teal flash on reload).
 */
export function restoreBrandColor(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* private mode / storage disabled — fall through to `default` */
  }
  applyBrandColor(normalizeBrandColorKey(stored));
}

/** Forget the tenant's brand color and fall back to the bundled teal. */
export function resetBrandColor(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / storage disabled — the reset below still applies */
  }
  applyBrandColor('default');
}
